// src/pages/Platform/AddEditPlatformUser.js
//
// Create/edit modal for one platform-staff login (handlers.
// CreatePlatformUserHandler/UpdatePlatformUserHandler, task #202) — a
// single component handles both modes (existingUser present = edit), same
// "AddEditX" naming/shape convention the tenant app already uses for
// AddEditUser.js, just without the avatar/phone/job-title fields
// PlatformUser has none of (see that model's own doc comment).
import { useState, useEffect } from "react";
import { createPlatformUser, updatePlatformUser } from "../../services/api";

export default function AddEditPlatformUser({ token, existingUser, groups, onClose, onSaved }) {
  const isEdit = !!existingUser;
  const [form, setForm] = useState({
    email: existingUser?.email || "",
    password: "",
    first_name: existingUser?.first_name || "",
    last_name: existingUser?.last_name || "",
    is_active: existingUser ? existingUser.is_active : true,
    group_ids: existingUser?.groups?.map((g) => g.id) || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm({
      email: existingUser?.email || "",
      password: "",
      first_name: existingUser?.first_name || "",
      last_name: existingUser?.last_name || "",
      is_active: existingUser ? existingUser.is_active : true,
      group_ids: existingUser?.groups?.map((g) => g.id) || [],
    });
  }, [existingUser]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleGroup = (id) => {
    setForm((f) => ({
      ...f,
      group_ids: f.group_ids.includes(id) ? f.group_ids.filter((g) => g !== id) : [...f.group_ids, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email || (!isEdit && !form.password)) {
      setError("Email and password are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (isEdit && !payload.password) delete payload.password;
      if (isEdit) {
        await updatePlatformUser(token, existingUser.id, payload);
      } else {
        await createPlatformUser(token, payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message || "Failed to save platform user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        <form onSubmit={handleSubmit}>
          <h3 style={{ marginTop: 0 }}>{isEdit ? "Edit platform user" : "Add platform user"}</h3>

          <label style={labelStyle}>Email</label>
          <input style={inputStyle} type="email" value={form.email} onChange={set("email")} required />

          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>First name</label>
              <input style={inputStyle} value={form.first_name} onChange={set("first_name")} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Last name</label>
              <input style={inputStyle} value={form.last_name} onChange={set("last_name")} />
            </div>
          </div>

          <label style={labelStyle}>{isEdit ? "New password (leave blank to keep current)" : "Password"}</label>
          <input style={inputStyle} type="password" value={form.password} onChange={set("password")} />

          <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
            />
            Active
          </label>

          <label style={labelStyle}>Groups</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            {(groups || []).map((g) => (
              <label key={g.id} style={chipStyle(form.group_ids.includes(g.id))}>
                <input
                  type="checkbox"
                  checked={form.group_ids.includes(g.id)}
                  onChange={() => toggleGroup(g.id)}
                  style={{ marginRight: 6 }}
                />
                {g.name}
              </label>
            ))}
          </div>

          {error && <div style={errorBoxStyle}>{error}</div>}

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button type="button" style={secondaryBtnStyle} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" style={primaryBtnStyle} disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save changes" : "Create user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const chipStyle = (active) => ({
  display: "flex",
  alignItems: "center",
  fontSize: 13,
  padding: "6px 10px",
  borderRadius: 999,
  border: `1px solid ${active ? "#0f172a" : "#cbd5e1"}`,
  background: active ? "#0f172a" : "#fff",
  color: active ? "#fff" : "#334155",
  cursor: "pointer",
});
const inputStyle = { padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, width: "100%", boxSizing: "border-box" };
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", margin: "12px 0 4px" };
const primaryBtnStyle = { background: "#0f172a", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" };
const secondaryBtnStyle = { background: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: 8, padding: "10px 16px", fontSize: 14, cursor: "pointer" };
const errorBoxStyle = { background: "#fee2e2", color: "#991b1b", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 12 };
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 24, width: 420, maxWidth: "90vw", maxHeight: "90vh", overflowY: "auto" };
